/* Theme ArtWeb v1.0.0 · © Studio Art Web · https://studioartweb.com.br · não editar sem falar com o Studio */
/*
 * Harmony Health | hh-motion.js
 * Movimento do tema, sem biblioteca (estilos em assets/hh-motion.css).
 *
 * 1. Entradas ao rolar: elementos com data-hh-anima ("secao", "card",
 *    "imagem", "fade") entram com esmaecer e deslocamento pequeno, uma vez só.
 *    - Um único IntersectionObserver para a página toda, sem listener de rolagem.
 *    - A primeira dobra não anima: o que já está na tela (ou acima) quando o
 *      script roda fica como está. Só o que está abaixo ganha .hh-anima-espera.
 *    - Disparo cedo (rootMargin de 80 px abaixo da tela) para nunca ver área vazia;
 *      margem enorme acima, para revelar na hora o que foi pulado por um salto.
 *    - Escalonamento curto entre irmãos que entram juntos (45 ou 60 ms, teto de
 *      270 ou 300 ms).
 *    - Segurança: sem resposta do observador em 2,5 s, revela tudo. Impressão
 *      revela tudo. Movimento reduzido ou entradas desligadas no editor: nada
 *      é escondido.
 *    - Conteúdo carregado depois (recomendações, kits, sacola, editor de temas)
 *      entra pelo MutationObserver e pelos eventos shopify:section:load.
 * 2. Pequenas confirmações de estado:
 *    - contador da sacola (cabeçalho e navegação do celular) pulsa de leve no
 *      cart:update;
 *    - valores dentro de [data-hh-anima-troca] marcados com [data-hh-anima-valor]
 *      esmaecem ao trocar a escolha (1, 3 ou 6 frascos, Clube).
 * Configuração: html[data-hh-entradas="sim|nao"] e html[data-hh-mov="suave|normal"],
 * vindos de Configurações do tema > Harmony Health: animações.
 */

const html = document.documentElement;
const reduz = window.matchMedia('(prefers-reduced-motion: reduce)');
const SELETOR = '[data-hh-anima]';
const ESPERA = 'hh-anima-espera';
const ANDANDO = 'hh-anima-andando';
const RAPIDA = 'hh-anima-rapida';

/** Elementos já tratados (revelados ou em espera), para não tratar duas vezes. */
const tratados = new WeakSet();
/** Elementos escondidos esperando entrar na tela. */
const pendentes = new Set();

let io = null;
let ativo = false;
let respondeu = false;
let tempos = { dur: 340, passo: 45, teto: 270 };

const ms = (estilo, nome, padrao) => {
  const v = parseFloat(estilo.getPropertyValue(nome));
  return Number.isFinite(v) ? v : padrao;
};

function lerTempos() {
  const estilo = getComputedStyle(html);
  tempos = {
    dur: ms(estilo, '--hh-mov-entrada', 340),
    passo: ms(estilo, '--hh-mov-escalona', 45),
    teto: ms(estilo, '--hh-mov-teto', 270),
  };
}

const entradasPermitidas = () =>
  html.dataset.hhEntradas !== 'nao' && !reduz.matches && 'IntersectionObserver' in window;

/** Ordem do documento, para escalonar de cima para baixo. */
const ordem = (a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);

/** Revela sem transição (primeira dobra, falha, impressão). */
function mostra(el) {
  tratados.add(el);
  pendentes.delete(el);
  io?.unobserve(el);
  const andava = el.classList.contains(ANDANDO);
  el.classList.remove(ESPERA, ANDANDO, RAPIDA);
  el.style.removeProperty('--hh-anima-atraso');
  // Tirar a classe não para uma transição já em curso no Chrome: termina na hora
  // (foco de teclado chegando num cartão que ainda está surgindo).
  if (andava && typeof el.getAnimations === 'function') {
    for (const a of el.getAnimations()) {
      if (a.transitionProperty === 'opacity' || a.transitionProperty === 'transform') a.finish();
    }
  }
}

/**
 * Revela com a transição de entrada; will-change só enquanto anda.
 * @param {Element} el
 * @param {number} atraso escalonamento em ms
 * @param {Array<[Element, number]>} [lote] limpeza em conjunto (ver limpaDepois)
 */
function anima(el, atraso, lote) {
  pendentes.delete(el);
  io?.unobserve(el);
  if (!el.classList.contains(ESPERA)) return;
  if (atraso > 0) el.style.setProperty('--hh-anima-atraso', `${atraso}ms`);
  // A transição usa as propriedades do estilo novo, então pôr .hh-anima-andando
  // e tirar .hh-anima-espera no mesmo quadro basta (o estado escondido já foi
  // desenhado antes). Sem esperar um quadro a mais: em celular lento, cada
  // quadro de espera é conteúdo que demora a aparecer.
  el.classList.add(ANDANDO);
  el.classList.remove(ESPERA);
  if (lote) lote.push([el, atraso]);
  else limpaDepois([[el, atraso]]);
}

/**
 * Tira will-change e a classe de transição de um grupo de uma vez só, quando o
 * último termina. Cada troca de camada repinta a página; juntar o grupo num
 * único momento corta essas repinturas durante a rolagem.
 * @param {Array<[Element, number]>} lote
 */
function limpaDepois(lote) {
  if (!lote.length) return;
  const maior = lote.reduce((m, [, a]) => Math.max(m, a), 0);
  window.setTimeout(() => {
    for (const [el] of lote) {
      el.classList.remove(ANDANDO, RAPIDA);
      el.style.removeProperty('--hh-anima-atraso');
    }
  }, tempos.dur + maior + 120);
}

function revelaTudo() {
  pendentes.forEach(mostra);
  pendentes.clear();
}

function aoCruzar(entradas) {
  respondeu = true;
  const entram = [];
  const lote = [];
  for (const e of entradas) {
    if (!e.isIntersecting) continue;
    // A margem de cima do observador cobre tudo que está acima da tela. Um
    // elemento que só "cruza" ali foi pulado (salto para o rodapé, âncora,
    // restauração da rolagem): aparece na hora, sem animação.
    if (e.boundingClientRect.bottom <= 0) mostra(e.target);
    // Entrada tardia: só cruzou quando já estava bem dentro da tela (Page Down,
    // rolagem muito rápida). Sem deslocamento nem escalonamento, só um esmaecer
    // curto, para a área não ficar vazia à vista de quem lê.
    else if (e.boundingClientRect.top < (window.innerHeight || html.clientHeight) * 0.7) {
      e.target.classList.add(RAPIDA);
      anima(e.target, 0, lote);
    } else entram.push(e.target);
  }
  entram.sort(ordem);
  const porGrupo = new Map();
  for (const el of entram) {
    const grupo = el.parentElement;
    const k = porGrupo.get(grupo) || 0;
    porGrupo.set(grupo, k + 1);
    anima(el, Math.min(k * tempos.passo, tempos.teto), lote);
  }
  limpaDepois(lote);
}

/**
 * Prepara os elementos com data-hh-anima dentro de uma raiz.
 * @param {ParentNode} raiz
 * @param {'inicio' | 'depois' | 'editor'} modo inicio: primeira dobra fica parada;
 *   depois: conteúdo carregado depois que já está na tela entra com esmaecer;
 *   editor: o que está na tela fica parado (o lojista vê a mudança na hora).
 */
function registra(raiz, modo) {
  if (!raiz || !(raiz instanceof Element || raiz instanceof Document)) return;
  const lista = [];
  if (raiz instanceof Element && raiz.matches(SELETOR)) lista.push(raiz);
  raiz.querySelectorAll(SELETOR).forEach((el) => lista.push(el));
  const novos = lista.filter((el) => !tratados.has(el));
  if (!novos.length) return;
  novos.forEach((el) => tratados.add(el));
  if (!ativo) return;

  // leitura em lote, depois escrita em lote
  const alto = window.innerHeight || html.clientHeight;
  const caixas = novos.map((el) => el.getBoundingClientRect());
  const naTela = [];
  novos.forEach((el, i) => {
    const r = caixas[i];
    const semCaixa = r.width === 0 && r.height === 0;
    const jaPassou = !semCaixa && r.top < alto;
    if (jaPassou && modo !== 'depois') return; // primeira dobra ou editor: fica como está
    if (jaPassou && r.bottom <= 0) return; // chegou acima da tela: não há o que mostrar entrando
    el.classList.add(ESPERA);
    pendentes.add(el);
    if (jaPassou) naTela.push(el);
    else io.observe(el);
  });
  // conteúdo novo já na tela: entra no próximo quadro, escalonado
  if (naTela.length) {
    // lê a opacidade uma vez para o estado escondido valer como ponto de
    // partida da transição (só recalcula estilo, sem layout)
    void getComputedStyle(naTela[0]).opacity;
    const porGrupo = new Map();
    const lote = [];
    naTela.sort(ordem).forEach((el) => {
      const k = porGrupo.get(el.parentElement) || 0;
      porGrupo.set(el.parentElement, k + 1);
      anima(el, Math.min(k * tempos.passo, tempos.teto), lote);
    });
    limpaDepois(lote);
  }
}

function desliga() {
  ativo = false;
  io?.disconnect();
  revelaTudo();
  html.classList.remove('hh-anima-pronto');
}

function inicia() {
  ativo = entradasPermitidas();
  if (!ativo) {
    registra(document, 'inicio');
    return;
  }
  lerTempos();
  // embaixo: dispara 80 px antes de entrar na tela; em cima: margem enorme para
  // que um elemento pulado acima da tela também avise e seja revelado
  io = new IntersectionObserver(aoCruzar, { rootMargin: '100000px 0px 80px 0px', threshold: 0 });
  registra(document, 'inicio');
  html.classList.add('hh-anima-pronto');

  // segurança: se o observador não responder, nada fica escondido
  window.setTimeout(() => {
    if (!respondeu && pendentes.size) desliga();
  }, 2500);

  // conteúdo inserido depois (recomendações, kits, sacola, filtros)
  const mo = new MutationObserver((registros) => {
    for (const r of registros) {
      r.addedNodes.forEach((n) => {
        if (n instanceof Element && (n.matches(SELETOR) || n.querySelector(SELETOR))) registra(n, 'depois');
      });
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
}

// Editor de temas: seção nova ou refeita, seção ou bloco escolhido.
document.addEventListener('shopify:section:load', (e) => registra(e.target, 'editor'));
document.addEventListener('shopify:section:select', (e) => {
  if (!(e.target instanceof Element)) return;
  e.target.querySelectorAll(`.${ESPERA}`).forEach(mostra);
});
document.addEventListener('shopify:block:select', (e) => {
  const sec = e.target instanceof Element ? e.target.closest('.shopify-section') : null;
  sec?.querySelectorAll(`.${ESPERA}`).forEach(mostra);
});

// Foco de teclado (ou de leitor de tela) dentro de algo que ainda espera ou
// está entrando: termina a entrada na hora, para o contorno de foco nunca
// aparecer esmaecido.
document.addEventListener(
  'focusin',
  (e) => {
    let el = e.target instanceof Element ? e.target.closest(`.${ESPERA}, .${ANDANDO}`) : null;
    while (el) {
      mostra(el);
      el = el.parentElement?.closest(`.${ESPERA}, .${ANDANDO}`) ?? null;
    }
  },
  true
);

window.addEventListener('beforeprint', revelaTudo);
reduz.addEventListener?.('change', () => {
  if (reduz.matches) desliga();
});

/* ---------- 2. confirmações de estado ---------- */

const pulsoQuadros = [{ transform: 'scale(1)' }, { transform: 'scale(1.16)' }, { transform: 'scale(1)' }];

function pulsaContadores() {
  if (reduz.matches) return;
  document
    .querySelectorAll('.header-actions__cart-icon .cart-bubble, hh-nav-inferior [data-hh-contador]:not([hidden])')
    .forEach((el) => {
      if (!(el instanceof HTMLElement) || typeof el.animate !== 'function') return;
      el.animate(pulsoQuadros, { duration: 320, easing: 'cubic-bezier(.2,.7,.2,1)' });
    });
}

// o contador do cabeçalho e o da navegação se atualizam logo depois do evento
document.addEventListener('cart:update', () => window.setTimeout(pulsaContadores, 450));

const trocaQuadros = [
  { opacity: 0.35, transform: 'translate3d(0,2px,0)' },
  { opacity: 1, transform: 'none' },
];
const trocaReduzida = [{ opacity: 0.6 }, { opacity: 1 }];

document.addEventListener('change', (e) => {
  const alvo = e.target instanceof Element ? e.target : null;
  const raiz = alvo?.closest('[data-hh-anima-troca]');
  if (!raiz) return;
  raiz.querySelectorAll('[data-hh-anima-valor]').forEach((el) => {
    if (!(el instanceof HTMLElement) || typeof el.animate !== 'function') return;
    el.animate(reduz.matches ? trocaReduzida : trocaQuadros, {
      duration: reduz.matches ? 120 : 220,
      easing: 'cubic-bezier(.2,.7,.2,1)',
    });
  });
});

/* ---------- 3. sacola lateral: fechar devolve a rolagem ----------
   No celular o Horizon (cart-drawer.js) cria uma entrada no histórico ao abrir
   a sacola e volta (history.back) ao fechar. A entrada nasce com a página
   travada (body fixo, rolagem 0), então a volta restaurava a rolagem 0 e a
   página subia até o topo logo depois de fechar. Enquanto a sacola está
   aberta, a restauração automática fica desligada; na volta, a posição real é
   devolvida na hora. */
let rolagemSacola = null;
let restauraModo = 0;
const ehSacola = (e) => e.target instanceof Element && e.target.localName === 'cart-drawer-component';
const modoAuto = () => {
  try {
    history.scrollRestoration = 'auto';
  } catch (_) {}
};
document.addEventListener(
  'dialog:open',
  (e) => {
    if (!ehSacola(e)) return;
    // body.style.top = -Y (dialog.js): é a posição real enquanto a página está travada
    const y = -parseFloat(document.body.style.top || '0');
    rolagemSacola = Number.isFinite(y) ? y : window.scrollY;
    window.clearTimeout(restauraModo);
    try {
      history.scrollRestoration = 'manual';
    } catch (_) {}
  },
  true
);
document.addEventListener(
  'dialog:close',
  (e) => {
    if (!ehSacola(e)) return;
    // a volta do histórico chega logo depois; sem ela (computador), só religa
    window.clearTimeout(restauraModo);
    restauraModo = window.setTimeout(() => {
      rolagemSacola = null;
      modoAuto();
    }, 1000);
  },
  true
);
window.addEventListener('popstate', () => {
  if (rolagemSacola === null) return;
  if (document.querySelector('cart-drawer-component dialog[open]')) return; // o Horizon fecha e devolve
  const y = rolagemSacola;
  rolagemSacola = null;
  const volta = () => {
    if (Math.abs(window.scrollY - y) > 1) window.scrollTo({ top: y, behavior: 'instant' });
  };
  volta();
  requestAnimationFrame(() => {
    volta();
    requestAnimationFrame(() => {
      volta();
      window.clearTimeout(restauraModo);
      modoAuto();
    });
  });
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', inicia, { once: true });
else inicia();
